'use client';

import { c as _c } from "react/compiler-runtime";
import { createBackerRewardScope, type GetBackerRewardsRow, useGetBackerRewardsInfinite } from '@morfi/backend-sdk-v2/client';
import type { RefCallback } from 'react';
import { BACKER_REWARDS_FIELDS } from '@/components/portfolio/cards/backer-rewards-fields';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
export interface CreatorBackerRewards {
  /** Lifetime share (D6) for a market, `'0'` when never backed, `undefined` while loading. */
  readonly backerRewardsFor: (marketPubkey: string) => string | undefined;
  /**
   * Per-market viewport ref from the backer-rewards list. The Created row must
   * mount it on a plain element so that market's trade and claim subjects stay
   * registered while the row is on screen; the rewards card further down the
   * page may be off screen, and only registered rows receive live patches.
   */
  readonly observeBackerRewardsRow: (marketPubkey: string) => RefCallback<Element>;
}

/**
 * The viewed profile's own lifetime backer rewards (unclaimed + claimed, D6)
 * per market. The Created list must show the creator's share, not the
 * market-wide `backerRewards` total every backer contributes to.
 *
 * Mirrors the `BackerRewardsCard` query on the same profile exactly (same
 * args, same fields) so the two surfaces share one fetch and one realtime
 * patched cache entry. Returns `undefined` for every market until that list
 * has loaded; a market without a row is one the creator never backed.
 */
export function useCreatorBackerRewards(t0) {
  const $ = _c(21);
  const loadEnabled = t0 === undefined ? true : t0;
  const ctx = usePortfolioContext();
  const enabled = loadEnabled && ctx.financialQueryId !== "";
  const t1 = ctx.financialQueryId;
  let t2;
  if ($[0] !== ctx.userId || $[1] !== ctx.walletPubkeys) {
    t2 = createBackerRewardScope(ctx.userId, ctx.walletPubkeys);
    $[0] = ctx.userId;
    $[1] = ctx.walletPubkeys;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== ctx.walletPubkeys) {
    t3 = ctx.walletPubkeys.join(",");
    $[3] = ctx.walletPubkeys;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [...BACKER_REWARDS_FIELDS];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== ctx.financialQueryId || $[7] !== t2 || $[8] !== t3) {
    t5 = {
      userId: t1,
      rewardScope: t2,
      walletPubkeysCsv: t3,
      fields: t4
    };
    $[6] = ctx.financialQueryId;
    $[7] = t2;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== enabled) {
    t6 = {
      enabled
    };
    $[10] = enabled;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const query = useGetBackerRewardsInfinite(t5, t6);
  const rows = useLiveListData(query.queryKey, query.rows, query.entityQueryKey);
  const observeBackerRewardsRow = query.itemObserver;
  if (enabled && query.isPending) {
    let t7;
    if ($[12] !== observeBackerRewardsRow) {
      t7 = {
        backerRewardsFor: _temp,
        observeBackerRewardsRow
      };
      $[12] = observeBackerRewardsRow;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  let lifetimeByMarket;
  if ($[14] !== rows) {
    lifetimeByMarket = new Map();
    for (const row of rows) {
      lifetimeByMarket.set(row.marketPubkey, (BigInt(row.earnedD6) + BigInt(row.claimedTotalD6)).toString());
    }
    $[14] = rows;
    $[15] = lifetimeByMarket;
  } else {
    lifetimeByMarket = $[15];
  }
  let t7;
  if ($[16] !== lifetimeByMarket) {
    t7 = marketPubkey => lifetimeByMarket.get(marketPubkey) ?? "0";
    $[16] = lifetimeByMarket;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== observeBackerRewardsRow || $[19] !== t7) {
    t8 = {
      backerRewardsFor: t7,
      observeBackerRewardsRow
    };
    $[18] = observeBackerRewardsRow;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp() {}